import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Building2, Loader2, Ticket } from 'lucide-react';

export default function Register() {
  const [inviteCode, setInviteCode] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!inviteCode.trim()) {
      toast.error('Bitte geben Sie einen Einladungscode ein');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }

    if (password.length < 6) {
      toast.error('Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }

    setLoading(true);

    try {
      await register(name, email, password, inviteCode);
      toast.success('Erfolgreich registriert');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registrierung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{ 
          backgroundImage: 'url(https://images.unsplash.com/photo-1769890893164-8737168ed463?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920)'
        }}
      >
        <div className="absolute inset-0 bg-slate-900/60" />
        <div className="relative z-10 p-12 flex flex-col justify-end text-white">
          <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Willkommen bei Kivera
          </h1>
          <p className="text-lg text-slate-200">
            Sie wurden eingeladen, Kivera zu nutzen. Erstellen Sie jetzt Ihr Konto 
            und beginnen Sie mit der professionellen Verwaltung Ihrer Immobilien.
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <Card className="w-full max-w-md border-slate-200 shadow-lg">
          <CardHeader className="text-center pb-2">
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center">
                <Building2 className="w-8 h-8 text-emerald-600" />
              </div>
            </div>
            <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Konto erstellen
            </CardTitle>
            <CardDescription>
              Registrierung nur mit Einladungscode möglich
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="inviteCode" className="flex items-center gap-2">
                  <Ticket className="h-4 w-4 text-emerald-600" />
                  Einladungscode <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="inviteCode"
                  type="text"
                  data-testid="register-invite-code-input"
                  placeholder="XXXXX-XXXXX"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                  className="font-mono tracking-wider"
                  required
                />
              </div>
              <div className="border-t border-slate-200 pt-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name <span className="text-red-500">*</span></Label>
                  <Input
                    id="name"
                    type="text"
                    data-testid="register-name-input"
                    placeholder="Max Mustermann"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">E-Mail <span className="text-red-500">*</span></Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="register-email-input"
                  placeholder="ihre@email.de"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Passwort <span className="text-red-500">*</span></Label>
                <Input
                  id="password"
                  type="password"
                  data-testid="register-password-input"
                  placeholder="Mindestens 6 Zeichen"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Passwort bestätigen <span className="text-red-500">*</span></Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  data-testid="register-confirm-password-input"
                  placeholder="Passwort wiederholen"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
              <Button 
                type="submit" 
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                data-testid="register-submit-button"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Registrieren...
                  </>
                ) : (
                  'Registrieren'
                )}
              </Button>
            </form>
            <div className="mt-6 text-center text-sm text-slate-600">
              Bereits ein Konto?{' '}
              <Link 
                to="/login" 
                className="text-emerald-600 hover:text-emerald-700 font-medium"
                data-testid="login-link"
              >
                Jetzt anmelden
              </Link>
            </div>
            <div className="mt-4 p-3 bg-slate-50 rounded-lg text-xs text-slate-500 text-center">
              Keinen Einladungscode? Kontaktieren Sie uns unter{' '}
              <a href="mailto:info@kivera.de" className="text-emerald-600 hover:underline">
                info@kivera.de
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
